import { forwardRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { cn } from "../lib/utils";
import { calendarClassName, datePickerClassName } from "./datepicker";

interface DayPickerProps {
  value?: Date | null;
  onChange: (date: Date | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
}

const DayPicker = forwardRef<DatePicker, DayPickerProps>(
  (
    {
      value,
      onChange,
      placeholder = "MM/DD/YYYY",
      className,
      disabled,
      minDate,
      maxDate,
    },
    ref
  ) => {
    const formatDateInput = (numericInput: string): string => {
      let formatted = "";
      if (numericInput.length >= 1) {
        formatted = numericInput.substring(0, Math.min(2, numericInput.length));
        if (numericInput.length >= 3) {
          formatted +=
            "/" + numericInput.substring(2, Math.min(4, numericInput.length));
          if (numericInput.length >= 5) {
            formatted +=
              "/" + numericInput.substring(4, Math.min(8, numericInput.length));
          }
        }
      }
      return formatted;
    };

    const validateDateComponents = (
      month: string,
      day: string,
      year: string
    ): boolean => {
      const monthNum = parseInt(month, 10);
      const dayNum = parseInt(day, 10);
      const yearNum = parseInt(year, 10);
      const currentYear = new Date().getFullYear();

      if (monthNum < 1 || monthNum > 12) return false;

      if (yearNum < 1900 || yearNum > currentYear) return false;

      const daysInMonth = new Date(yearNum, monthNum, 0).getDate();
      if (dayNum < 1 || dayNum > daysInMonth) return false;

      return true;
    };

    const handleInputChange = (e: React.FormEvent<HTMLInputElement>) => {
      const target = e.target as HTMLInputElement;
      const inputValue = target.value;

      const numericOnly = inputValue.replace(/\D/g, "");
      const limitedNumeric = numericOnly.substring(0, 8);

      if (limitedNumeric.length >= 2) {
        const month = limitedNumeric.substring(0, 2);
        const monthNum = parseInt(month, 10);
        if (monthNum > 12) {
          return;
        }
      }

      if (limitedNumeric.length >= 4) {
        const day = limitedNumeric.substring(2, 4);
        const dayNum = parseInt(day, 10);

        if (dayNum > 31) {
          return;
        }
      }

      if (limitedNumeric.length >= 8) {
        const month = limitedNumeric.substring(0, 2);
        const day = limitedNumeric.substring(2, 4);
        const year = limitedNumeric.substring(4, 8);
        const yearNum = parseInt(year, 10);
        const currentYear = new Date().getFullYear();

        if (yearNum < 1900 || yearNum > currentYear) {
          return;
        }

        if (!validateDateComponents(month, day, year)) {
          return;
        }
      }

      const formattedValue = formatDateInput(limitedNumeric);

      if (target.value !== formattedValue) {
        const selectionStart = target.selectionStart || 0;
        target.value = formattedValue;

        let newPosition = selectionStart;
        if (limitedNumeric.length >= 2 && selectionStart > 2) newPosition++;
        if (limitedNumeric.length >= 4 && selectionStart > 5) newPosition++;
        newPosition = Math.min(newPosition, formattedValue.length);
        target.setSelectionRange(newPosition, newPosition);
      }

      if (limitedNumeric.length === 8) {
        const month = limitedNumeric.substring(0, 2);
        const day = limitedNumeric.substring(2, 4);
        const year = limitedNumeric.substring(4, 8);

        if (validateDateComponents(month, day, year)) {
          const date = new Date(Number(year), Number(month) - 1, Number(day));
          if (!isNaN(date.getTime())) {
            onChange(date);
          }
        }
      }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pastedText = e.clipboardData.getData("text");

      const dateMatch = pastedText.match(
        /^(\d{2})[\/\-\.](\d{2})[\/\-\.](\d{4})$/
      );
      if (dateMatch) {
        const [_, month, day, year] = dateMatch;

        if (validateDateComponents(month, day, year)) {
          const date = new Date(Number(year), Number(month) - 1, Number(day));
          if (!isNaN(date.getTime())) {
            onChange(date);
          }
        }
      } else {
        const numericOnly = pastedText.replace(/\D/g, "");
        if (numericOnly.length === 8) {
          const month = numericOnly.substring(0, 2);
          const day = numericOnly.substring(2, 4);
          const year = numericOnly.substring(4, 8);

          if (validateDateComponents(month, day, year)) {
            const date = new Date(Number(year), Number(month) - 1, Number(day));
            if (!isNaN(date.getTime())) {
              onChange(date);
            }
          }
        }
      }
    };

    const handleDatePickerChange = (date: Date | null) => {
      if (date) {
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const year = String(date.getFullYear());

        if (validateDateComponents(month, day, year)) {
          onChange(date);
        }
      } else {
        onChange(null);
      }
    };

    return (
      <div className="day-picker-wrapper">
        <style
          dangerouslySetInnerHTML={{
            __html: `
            .day-picker-wrapper .react-datepicker__month-read-view,
            .day-picker-wrapper .react-datepicker__year-read-view {
              visibility: visible !important;
            }
          `,
          }}
        />
        <DatePicker
          selected={value}
          onChange={handleDatePickerChange}
          dateFormat="MM/dd/yyyy"
          placeholderText={placeholder}
          disabled={disabled}
          minDate={minDate}
          maxDate={maxDate}
          ref={ref}
          customInput={
            <input
              onInput={handleInputChange}
              onPaste={handlePaste}
              className={cn(datePickerClassName, className)}
            />
          }
          wrapperClassName="w-full"
          showMonthDropdown
          showYearDropdown
          dropdownMode="scroll"
          scrollableYearDropdown
          scrollableMonthYearDropdown
          yearDropdownItemNumber={150}
          adjustDateOnChange={false}
          showPopperArrow={false}
          popperPlacement="bottom-start"
          calendarClassName={calendarClassName.className}
        />
      </div>
    );
  }
);

DayPicker.displayName = "DayPicker";

export default DayPicker;
export { DayPicker as SimpleDayPicker };
